import React, { useState } from 'react';

/**
 * Componente Agnóstico de Kanban para o CRM PTS
 * @param {Array} columns - Definição das colunas [{ id: 'STATUS_ID', title: 'Nome Bonito', color: '#hex' }]
 * @param {Array} items - Os dados reais (Cartões) puxados do Firebase
 * @param {String} groupByKey - Qual campo do item diz em qual coluna ele está (ex: 'status' ou 'estagio_funil')
 * @param {Function} renderCard - Função que desenha o miolo do cartão (recebe o item como parâmetro)
 * @param {Function} onItemMove - (Opcional) Função chamada quando um cartão é solto em nova coluna
 * @param {Function} onCardClick - (Opcional) Ação ao clicar em um cartão
 */
const KanbanBoard = ({ 
    columns, 
    items, 
    groupByKey, 
    renderCard, 
    onItemMove, 
    onCardClick 
}) => {
    // Estado para saber qual item está sendo arrastado no momento
    const [draggingItem, setDraggingItem] = useState(null);

    // --- FUNÇÕES DE DRAG & DROP (HTML5 Nativo) ---
    const handleDragStart = (e, item) => {
        setDraggingItem(item);
        // Efeito visual sutil ao segurar o cartão
        setTimeout(() => {
            if (e.target) e.target.style.opacity = '0.5';
        }, 0);
    };

    const handleDragEnd = (e) => {
        if (e.target) e.target.style.opacity = '1';
        setDraggingItem(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessário para permitir o Drop
    };

    const handleDrop = (e, columnId) => {
        e.preventDefault();
        
        // Se soltou na mesma coluna, não faz nada
        if (!draggingItem || draggingItem[groupByKey] === columnId) {
            return;
        }

        // Chama a função do pai para salvar no banco de dados
        if (onItemMove) {
            onItemMove(draggingItem, columnId);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '20px', height: '100%', overflowX: 'auto', paddingBottom: '10px' }}>
            
            {columns.map(column => {
                // Filtra os itens que pertencem a esta coluna
                const columnItems = items.filter(item => item[groupByKey] === column.id);

                return (
                    <div 
                        key={column.id}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, column.id)}
                        style={{
                            flex: '0 0 320px', // Largura fixa das colunas
                            background: '#f8fafc',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '100%' // Respeita a altura do container pai
                        }}
                    >
                        {/* CABEÇALHO DA COLUNA */}
                        <div style={{ 
                            padding: '15px', 
                            borderBottom: `3px solid ${column.color || '#cbd5e1'}`,
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center'
                        }}>
                            <h3 style={{ fontSize: '0.95rem', color: '#0f172a', margin: 0, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {column.title}
                            </h3>
                            <span style={{ background: '#e2e8f0', color: '#475569', fontSize: '0.75rem', fontWeight: '800', padding: '3px 8px', borderRadius: '12px' }}>
                                {columnItems.length}
                            </span>
                        </div>

                        {/* ÁREA DE CARTÕES (Scrollável) */}
                        <div style={{ 
                            padding: '15px', 
                            flexGrow: 1, 
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {columnItems.length === 0 ? (
                                <div style={{ 
                                    padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', 
                                    border: '1px dashed #cbd5e1', borderRadius: '8px', background: 'transparent' 
                                }}>
                                    Nenhum item nesta etapa.
                                </div>
                            ) : (
                                columnItems.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        draggable={!!onItemMove} // Só é arrastável se o pai passar a função onItemMove
                                        onDragStart={(e) => handleDragStart(e, item)}
                                        onDragEnd={handleDragEnd}
                                        onClick={() => onCardClick && onCardClick(item)}
                                        style={{
                                            background: '#fff',
                                            padding: '15px',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            cursor: onItemMove ? 'grab' : (onCardClick ? 'pointer' : 'default'),
                                            transition: 'box-shadow 0.2s, transform 0.1s',
                                            userSelect: 'none' // Evita selecionar texto ao arrastar
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.08)'}
                                        onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
                                    >
                                        {/* A MÁGICA DO AGNOSTICISMO: O pai diz como o cartão se parece */}
                                        {renderCard ? renderCard(item) : (
                                            <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>⚠️ Forneça a prop renderCard</div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default KanbanBoard;